/* Bulletin Compliance Overview dashboard — iter68.
 * 12-month executive view of the student report card signature campaign.
 * Route: /bulletin-compliance   (admin / director_studies / rh).
 */
import { useEffect, useState } from 'react';
import { bulletinSignaturesApi } from '../lib/api';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart,
} from 'recharts';
import {
  ChartLine, GraduationCap, ArrowClockwise, FileText, EnvelopeSimple,
  Warning, TrendUp,
} from '@phosphor-icons/react';
import { BulletinKpiRow, BulletinVerdictCard } from '../components/bulletin/BulletinKpiCards';

const fmtMonth = (m) => {
  if (!m) return '—';
  try {
    const [y, mo] = m.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('fr-FR',
      { month: 'short', year: '2-digit' });
  } catch { return m; }
};

const fmtDuration = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} j`;
};

export default function BulletinComplianceOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [emailBusy, setEmailBusy] = useState(false);
  const [slaThreshold, setSlaThreshold] = useState(72);
  const [slaCheckBusy, setSlaCheckBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await bulletinSignaturesApi.complianceOverview(months);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [months]);

  const emailLastMonth = async () => {
    setEmailBusy(true);
    try {
      const { data } = await bulletinSignaturesApi.generateCompliance(null, true);
      const n = data.notified?.sent ?? 0;
      toast.success(`Rapport ${data.key} envoyé à ${n} destinataire(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
    } finally { setEmailBusy(false); }
  };

  const runSlaCheck = async () => {
    setSlaCheckBusy(true);
    try {
      const { data } = await bulletinSignaturesApi.slaAlertsCheck({
        thresholdHours: slaThreshold,
      });
      if (data.alerted > 0) {
        toast.success(
          `${data.alerted} classe(s) alertée(s) — ${data.breaches} dépassement(s) au seuil ${data.threshold_hours}h`,
        );
      } else if (data.breaches > 0) {
        toast.warning(
          `${data.breaches} dépassement(s) détecté(s) mais déjà notifié(s) cette semaine`,
        );
      } else {
        toast.success(`Aucun dépassement — toutes les classes sous ${data.threshold_hours}h ✓`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur vérification SLA');
    } finally { setSlaCheckBusy(false); }
  };

  const kpi = data?.kpi;

  return (
    <div className="p-6 space-y-6" data-testid="bulletin-compliance-overview-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
            <GraduationCap size={28} weight="duotone" className="text-[#0052FF]" />
            Conformité — Bulletins scolaires
          </h1>
          <p className="text-sm text-[#64748B]">
            Vision 12 mois de la campagne de signature des bulletins — signés (3/3),
            en attente, signatures partielles et élèves récurrents non-signants.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="h-9 rounded-md border border-[#E2E8F0] px-2 text-sm bg-white"
            data-testid="bul-months-selector"
          >
            <option value={3}>3 derniers mois</option>
            <option value={6}>6 derniers mois</option>
            <option value={12}>12 derniers mois</option>
            <option value={24}>24 derniers mois</option>
          </select>
          <Button variant="outline" onClick={load} data-testid="bul-refresh-btn">
            <ArrowClockwise size={14} className="mr-1" /> Rafraîchir
          </Button>
          <a
            href={bulletinSignaturesApi.complianceOverviewXlsxUrl(months)}
            className="inline-flex items-center h-9 px-3 rounded-md border border-emerald-400 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
            data-testid="bul-export-xlsx-btn"
          >
            <FileText size={14} className="mr-1" /> Export Excel
          </a>
          <Button
            onClick={emailLastMonth}
            disabled={emailBusy}
            className="bg-[#0052FF] hover:bg-[#0043D1]"
            data-testid="bul-email-report-btn"
          >
            <EnvelopeSimple size={14} className="mr-1" />
            {emailBusy ? 'Envoi…' : 'Envoyer rapport mois dernier'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-[#94A3B8] py-12">Chargement…</p>
      ) : !data ? (
        <p className="text-center text-red-600 py-12">Données indisponibles.</p>
      ) : (
        <>
          {/* Verdict card (3-second executive summary) */}
          <BulletinVerdictCard
            forecast={data.forecast}
            recurringCount={data.recurring_pending?.length ?? 0}
          />

          {/* KPI ROW */}
          <BulletinKpiRow kpi={kpi} />

          {/* iter69 — SLA (Service Level Agreement) card */}
          {(data.sla?.count ?? 0) > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-outfit font-semibold mb-3 flex items-center gap-2">
                  <ChartLine size={18} weight="duotone" className="text-[#0052FF]" />
                  SLA de signature — Temps jusqu'à 3/3 signatures
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                    <p className="text-[10px] uppercase text-[#64748B] font-bold">Bulletins signés</p>
                    <p className="text-2xl font-outfit font-bold" data-testid="bul-sla-count">
                      {data.sla.count}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE]">
                    <p className="text-[10px] uppercase text-[#0052FF] font-bold">Délai moyen</p>
                    <p className="text-2xl font-outfit font-bold text-[#0052FF]"
                      data-testid="bul-sla-overall-avg">
                      {fmtDuration(data.sla.avg_hours)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-[10px] uppercase text-emerald-700 font-bold">Médiane</p>
                    <p className="text-2xl font-outfit font-bold text-emerald-700"
                      data-testid="bul-sla-overall-median">
                      {fmtDuration(data.sla.median_hours)}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-[10px] uppercase text-amber-700 font-bold">P90 (9 sur 10)</p>
                    <p className="text-2xl font-outfit font-bold text-amber-700"
                      data-testid="bul-sla-overall-p90">
                      {fmtDuration(data.sla.p90_hours)}
                    </p>
                  </div>
                </div>

                {(data.sla_by_class?.length ?? 0) > 0 && (
                  <>
                    {/* iter71 — SLA breach alert controls */}
                    <div
                      className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-md bg-[#FEF2F2] border border-red-200"
                      data-testid="bul-sla-alert-controls"
                    >
                      <div className="flex items-center gap-2">
                        <Warning size={16} weight="bold" className="text-red-600" />
                        <span className="text-xs font-bold text-red-700 uppercase">
                          Alertes SLA proactives
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <label className="text-[#64748B]">Seuil P90 (heures)</label>
                        <input
                          type="number" min="1" max="720" step="1"
                          value={slaThreshold}
                          onChange={(e) => setSlaThreshold(Number(e.target.value) || 72)}
                          className="w-20 h-8 border border-red-200 rounded px-2 text-sm"
                          data-testid="bul-sla-threshold-input"
                        />
                      </div>
                      <Button
                        size="sm" onClick={runSlaCheck} disabled={slaCheckBusy}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="bul-sla-check-btn"
                      >
                        {slaCheckBusy ? 'Vérification…' : 'Vérifier & notifier les directeurs'}
                      </Button>
                      <p className="text-[11px] text-red-700/80 flex-1 basis-full">
                        Toute classe dont le P90 dépasse le seuil déclenche une notification
                        push aux directeurs (une seule par semaine par classe).
                      </p>
                    </div>

                    <p className="text-xs font-semibold text-[#64748B] uppercase mb-1">
                      Classement des classes par rapidité de signature
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="bul-sla-by-class-table">
                        <thead>
                          <tr className="border-b border-[#E2E8F0]">
                            <th className="text-left py-2 px-2 font-semibold text-[#64748B]">#</th>
                            <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Classe</th>
                            <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Signés</th>
                            <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Moyen</th>
                            <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Médiane</th>
                            <th className="text-right py-2 px-2 font-semibold text-[#64748B]">P90</th>
                            <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.sla_by_class.map((c, i) => {
                            const breach = (c.p90_hours ?? 0) > slaThreshold;
                            return (
                              <tr key={c.class_name}
                                className={`border-b border-[#F1F5F9] ${breach ? 'bg-red-50/40' : ''}`}
                                data-testid={`bul-sla-row-${c.class_name}`}
                                data-breach={breach ? 'true' : 'false'}>
                                <td className="py-2 px-2 text-xs text-[#94A3B8]">{i + 1}</td>
                                <td className="py-2 px-2 font-medium">{c.class_name}</td>
                                <td className="py-2 px-2 text-center">{c.count}</td>
                                <td className={`py-2 px-2 text-right font-semibold ${
                                  c.avg_hours <= 24 ? 'text-emerald-700'
                                    : c.avg_hours <= 72 ? 'text-amber-700'
                                    : 'text-red-700'
                                }`}>
                                  {fmtDuration(c.avg_hours)}
                                </td>
                                <td className="py-2 px-2 text-right text-emerald-700">
                                  {fmtDuration(c.median_hours)}
                                </td>
                                <td className={`py-2 px-2 text-right ${breach ? 'text-red-700 font-bold' : 'text-amber-700'}`}>
                                  {fmtDuration(c.p90_hours)}
                                </td>
                                <td className="py-2 px-2 text-center">
                                  {breach ? (
                                    <span
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase"
                                      data-testid={`bul-sla-breach-badge-${c.class_name}`}
                                    >
                                      <Warning size={10} weight="fill" /> Dépassement
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-emerald-600 font-medium">✓ OK</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* LINE CHART — signature rate evolution + forecast */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-2 flex items-center gap-2">
                <ChartLine size={18} weight="duotone" className="text-[#0052FF]" />
                Taux de signature (évolution + prévision 3 mois)
              </h3>
              <div style={{ width: '100%', height: 260, minWidth: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={[
                    ...data.months.map((m) => ({
                      label: fmtMonth(m.month),
                      historique: m.signature_rate,
                      prevision: null,
                    })),
                    ...(data.forecast || []).map((f, idx) => ({
                      label: fmtMonth(f.month) + ' *',
                      historique: idx === 0 && data.months.length
                        ? data.months[data.months.length - 1].signature_rate
                        : null,
                      prevision: f.predicted_rate,
                    })),
                  ]} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => v == null ? '—' : `${v}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="historique" stroke="#0052FF"
                      strokeWidth={2.5} name="Historique" dot={{ r: 4 }} connectNulls />
                    <Line type="monotone" dataKey="prevision" stroke="#F59E0B"
                      strokeWidth={2} strokeDasharray="5 5"
                      name="Prévision (régression linéaire)"
                      dot={{ r: 4, fill: '#F59E0B' }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {data.forecast && data.forecast.length > 0 && (
                <p className="text-[10px] text-[#94A3B8] mt-1">
                  * Prévision basée sur la tendance linéaire des mois actifs.
                </p>
              )}
            </CardContent>
          </Card>

          {/* COMPOSED CHART — bulletins émis + taux */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-2 flex items-center gap-2">
                <GraduationCap size={18} weight="duotone" className="text-emerald-600" />
                Bulletins émis / signés et taux
              </h3>
              <div style={{ width: '100%', height: 300, minWidth: 300 }}>
                <ResponsiveContainer>
                  <ComposedChart data={data.months.map((m) => ({
                    ...m, label: fmtMonth(m.month),
                  }))} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                      unit="%" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="signed" stackId="a"
                      fill="#059669" name="Signés (3/3)" />
                    <Bar yAxisId="left" dataKey="pending" stackId="a"
                      fill="#F59E0B" name="En attente" />
                    <Line yAxisId="right" type="monotone" dataKey="signature_rate"
                      stroke="#0052FF" strokeWidth={2} name="Taux signature (%)"
                      dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recurring non-signers table */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-3 flex items-center gap-2">
                <Warning size={18} weight="duotone" className="text-amber-600" />
                Élèves récurrents en attente ({data.recurring_pending.length})
              </h3>
              {data.recurring_pending.length === 0 ? (
                <p className="text-sm text-[#94A3B8] italic text-center py-6">
                  Aucun élève récurrent — parfait !
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="bul-recurring-table">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Élève</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Classe</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Bulletins non-signés</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Périodes concernées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recurring_pending.map((r) => (
                      <tr key={r.student_id} className="border-b border-[#F1F5F9]"
                        data-testid={`bul-recurring-row-${r.student_id}`}>
                        <td className="py-2 px-2 font-medium">{r.student_name}</td>
                        <td className="py-2 px-2 text-xs text-[#64748B]">{r.class_name}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                            r.unsigned_count >= 4
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {r.unsigned_count}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-[#64748B]">
                          {r.periods.slice(0, 5).map(fmtMonth).join(', ')}
                          {r.periods.length > 5 ? ` +${r.periods.length - 5}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Detailed monthly table */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-3 flex items-center gap-2">
                <FileText size={18} weight="duotone" className="text-[#0052FF]" />
                Détail mensuel
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Mois</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Total</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Signés</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Taux</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Parent / Prof / Dir.</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Relance J+2 / J+5</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.slice().reverse().map((m) => (
                      <tr key={m.month} className="border-b border-[#F1F5F9]">
                        <td className="py-2 px-2 font-medium">{fmtMonth(m.month)}</td>
                        <td className="py-2 px-2 text-center">{m.total}</td>
                        <td className="py-2 px-2 text-center text-emerald-700 font-semibold">
                          {m.signed}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className={`font-bold ${
                            m.signature_rate >= 80 ? 'text-emerald-700'
                              : m.signature_rate >= 50 ? 'text-amber-700'
                              : 'text-red-700'
                          }`}>
                            {m.signature_rate}%
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center text-xs">
                          {m.partial_parent} / {m.partial_teacher} / {m.partial_director}
                        </td>
                        <td className="py-2 px-2 text-center text-xs text-amber-700">
                          {m.reminded_j2} / {m.reminded_j5}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {m.total > 0 && (
                            <a
                              href={bulletinSignaturesApi.complianceReportUrl(m.month)}
                              target="_blank" rel="noopener noreferrer"
                              className="text-[#0052FF] hover:underline text-xs font-semibold"
                              data-testid={`bul-pdf-link-${m.month}`}
                            >
                              PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
